import {
  Button,
  LabeledDatePickerInput,
  LabeledInput,
  LabeledSelect,
} from "@apex/component-library";
import { Box, Icon, Stack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { FaTimes } from "react-icons/fa";

import { usePermissions } from "../../contexts/permissions";
import { hasRequiredPermissions } from "../../hoc/withPermissions/helpers/hasRequiredPermissions";
import { Permissions } from "../../permissions";
import { EventType, ExportToolSearchBarValues, Status } from "../../types";
import { getDatePickerFormattedDate } from "../../utils/dateFormatter";
import CallTypeSelect from "../CallTypeSelect";
import CorrCodeSelect from "../CorrCodeSelect/CorrCodeSelect";
import EventNameSelect from "../EventNameSelect";

export interface ExportToolSearchBarProps {
  onClear: () => void;
  isFetching?: boolean;
}

const ExportToolSearchBar = ({
  onClear,
  isFetching,
}: ExportToolSearchBarProps) => {
  const formik = useFormikContext<ExportToolSearchBarValues>();

  // Space needed to show error message correctly
  const HEIGHT_FOR_ERROR = "5.3rem";

  const {
    values,
    isValid,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    handleChange,
    submitForm,
    setValues,
    resetForm,
  } = formik;

  const { permissions } = usePermissions();
  const canViewCorrCodeFilter = hasRequiredPermissions(permissions, [
    Permissions.MarginProcessingSearchFull,
  ]);

  const handleSetDate = (fieldName: string, date: string | Date) => {
    setFieldValue(fieldName, getDatePickerFormattedDate(date));
  };

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    submitForm();
  };
  return (
    <Stack
      backgroundColor="apex.whiteSmoke"
      borderBottomColor="apex.darkGray"
      borderBottomWidth={1}
      padding="1rem"
      pt={8}
      spacing={6}
    >
      <Stack spacing={4}>
        <Text as="h1" textStyle="h1">
          Margin Export Tool
        </Text>
        <Text>
          The Calls & Alerts view displays a maximum of 5,000 records. Use this
          tool for data sets greater than 5,000 records.
        </Text>
      </Stack>
      <Box as="form" width="100%" onSubmit={onSubmit}>
        <Wrap align="flex-end" spacing="1rem" width="100%">
          <WrapItem h={HEIGHT_FOR_ERROR} width="input.sm">
            <LabeledSelect
              label="Type"
              name="eventType"
              value={values.eventType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.eventType && !!errors.eventType}
              errorMessage={errors.eventType}
            >
              <option value={EventType.Null}>{""}</option>
              <option value={EventType.Alert}>Alert</option>
              <option value={EventType.Call}>Call</option>
            </LabeledSelect>
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR} width="input.sm">
            <LabeledSelect
              label="Status"
              name="status"
              value={values.status}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.status && !!errors.status}
              errorMessage={errors.status}
            >
              <option value={Status.All}>{""}</option>
              <option value={Status.Open}>Open</option>
              <option value={Status.Closed}>Closed</option>
            </LabeledSelect>
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR} width="input.md">
            <LabeledInput
              label="Account ID"
              name="accountId"
              data-testid="account-id"
              value={values.accountId}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.accountId && !!errors.accountId}
              errorMessage={errors?.accountId}
            />
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR}>
            <Stack direction="row" spacing="1rem" alignItems="center">
              <Box h={HEIGHT_FOR_ERROR}>
                <LabeledDatePickerInput
                  label="From (Trade Date)"
                  name="fromTradeDate"
                  onChange={(date: string | Date) => {
                    handleSetDate("fromTradeDate", date);
                  }}
                  onBlur={handleBlur}
                  date={values.fromTradeDate || ""}
                  placement="bottom"
                  isInvalid={touched.fromTradeDate && !!errors.fromTradeDate}
                  errorMessage={errors.fromTradeDate}
                  disableFutureDates
                  data-testid="from-trade-date-input"
                />
              </Box>
              <Box d="flex">
                <Text as="p" data-testid="amount-spacer" paddingTop="0.8rem">
                  &mdash;
                </Text>
              </Box>
              <Box width="input.md" h={HEIGHT_FOR_ERROR}>
                <LabeledDatePickerInput
                  label="To (Trade Date)"
                  name="toTradeDate"
                  onChange={(date: string | Date) => {
                    handleSetDate("toTradeDate", date);
                  }}
                  onBlur={handleBlur}
                  date={values.toTradeDate}
                  placement="bottom"
                  isInvalid={touched.toTradeDate && !!errors.toTradeDate}
                  errorMessage={errors.toTradeDate}
                  disableFutureDates
                  data-testid="to-trade-date-input"
                />
              </Box>
            </Stack>
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR}>
            <Stack direction="row" spacing="0.5rem" alignItems="center">
              <Box width="input.md" h={HEIGHT_FOR_ERROR}>
                <LabeledDatePickerInput
                  label="From (Due Date)"
                  name="fromDueDate"
                  onChange={(date: string | Date) => {
                    handleSetDate("fromDueDate", date);
                  }}
                  onBlur={handleBlur}
                  date={values.fromDueDate}
                  placement="bottom"
                  isInvalid={!!errors.fromDueDate}
                  errorMessage={errors.fromDueDate}
                  disableFutureDates
                  data-testid="from-due-date-input"
                />
              </Box>
              <Box d="flex">
                <Text as="p" data-testid="amount-spacer" paddingTop="0.8rem">
                  &mdash;
                </Text>
              </Box>
              <Box width="input.md" h={HEIGHT_FOR_ERROR}>
                <LabeledDatePickerInput
                  label="To (Due Date)"
                  name="toDueDate"
                  onChange={(date: string | Date) => {
                    handleSetDate("toDueDate", date);
                  }}
                  onBlur={handleBlur}
                  date={values.toDueDate}
                  placement="bottom"
                  isInvalid={!!errors.toDueDate}
                  errorMessage={errors.toDueDate}
                  disableFutureDates
                  data-testid="to-due-date-input"
                />
              </Box>
            </Stack>
          </WrapItem>
          {canViewCorrCodeFilter && (
            <WrapItem h={HEIGHT_FOR_ERROR} width="input.md">
              <CorrCodeSelect />
            </WrapItem>
          )}
          <WrapItem h={HEIGHT_FOR_ERROR} width="input.md">
            <CallTypeSelect />
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR} width="input.lg">
            <EventNameSelect />
          </WrapItem>
          <WrapItem h={HEIGHT_FOR_ERROR}>
            <Stack direction="row" spacing="0.5rem" alignItems="center">
              <Box width="input.md" h={HEIGHT_FOR_ERROR}>
                <LabeledInput
                  label="Amount"
                  name="amountMinDisplay"
                  iconLeft="$"
                  isFormattedNumber
                  placeholder="Min"
                  value={values.amountMinDisplay}
                  onChange={({ display, raw }) => {
                    setValues({
                      ...values,
                      amountMinDisplay: display,
                      amountMinRaw: raw ?? undefined,
                    });
                  }}
                  data-testid="amount-min-input"
                  onBlur={handleBlur}
                  isInvalid={
                    touched.amountMinDisplay &&
                    (!!errors.amountMinDisplay || !!errors.amountMinRaw)
                  }
                  errorMessage={errors.amountMinDisplay || errors.amountMinRaw}
                />
              </Box>
              <Box d="flex">
                <Text as="p" data-testid="amount-spacer" paddingTop="0.8rem">
                  &mdash;
                </Text>
              </Box>
              <Box width="input.md" h={HEIGHT_FOR_ERROR}>
                <LabeledInput
                  label="&zwnj;"
                  name="amountMaxDisplay"
                  iconLeft="$"
                  isFormattedNumber
                  placeholder="Max"
                  value={values.amountMaxDisplay}
                  onChange={({ display, raw }) => {
                    setValues({
                      ...values,
                      amountMaxDisplay: display,
                      amountMaxRaw: raw ?? undefined,
                    });
                  }}
                  data-testid="amount-max-input"
                  onBlur={handleBlur}
                  isInvalid={
                    touched.amountMaxDisplay &&
                    (!!errors.amountMaxDisplay || !!errors.amountMaxRaw)
                  }
                  errorMessage={errors.amountMaxDisplay || errors.amountMaxRaw}
                />
              </Box>
            </Stack>
          </WrapItem>
          <WrapItem
            h={HEIGHT_FOR_ERROR}
            width="120px"
            alignItems="center"
            paddingTop="1rem"
          >
            <Stack direction="row" spacing="0.5rem" alignItems="center">
              <Button
                variant="primary"
                type="submit"
                isDisabled={!isValid}
                isLoading={!!isFetching}
                data-testid="search-btn"
              >
                Download CSV File
              </Button>
              <Button
                data-testid="clear-form"
                variant="link"
                onClick={() => {
                  onClear();
                  resetForm();
                }}
              >
                <Icon as={FaTimes} pr="5px" /> Clear Form
              </Button>
            </Stack>
          </WrapItem>
        </Wrap>
      </Box>
    </Stack>
  );
};

export default ExportToolSearchBar;
